import {Box, createStyles, Grid, makeStyles} from '@material-ui/core';
import {PersonOutline, WorkOutline} from '@material-ui/icons';
import clsx from 'clsx';
import {ReactNode, useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {CustomTextField, Loading} from '../..';
import {BuyerDataType} from '../../../const/buyerDataType';
import i18nNamespaces from '../../../const/i18nNamespaces';
import {mobile} from '../../../const/sizes';
import CustomButton from '../../common/Button/Button';
import useStyles from './BuyerDataFormStyles';
import {BuyerDataFormConfig} from '../../../hooks/useBuyerDataFrom';
import buyerDataProvider from '../../../services/buyerDataProvider'
import axios from 'axios';
type BuyerDataFormProps = {
    buyerDataFormConfig: BuyerDataFormConfig;
    className?: string;
};

const BuyerDataForm = ({
    buyerDataFormConfig,
    className,
}: BuyerDataFormProps) => {
    const {
        handleChange,
        handleBlur,
        setFieldValue,
        resetForm,
        values,
        errors,
        touched,
    } = buyerDataFormConfig;

    const [loading, setLoading] = useState<boolean>(false);

    const switchBuyerDataType = (type: BuyerDataType) => {
        resetForm();
        setFieldValue('type', type);
    };

    const {t} = useTranslation(i18nNamespaces.TIME_SLOTS_PAYMENT);

    const classes = useStyles();

    const individualPersonRequiredIndicator =
        values.type === ('IndividualPerson' as BuyerDataType) && '*';

    const companyRequiredIndicator =
        values.type === ('Company' as BuyerDataType) && '*';

    const individualPersonButtonClasses = clsx(
        classes.buyerDataTypeButton,
        values.type !== ('IndividualPerson' as BuyerDataType) &&
            classes.disabledButton,
    );

    const companyButtonClasses = clsx(
        classes.buyerDataTypeButton,
        values.type !== ('Company' as BuyerDataType) && classes.disabledButton,
    );

    const fetchData = async () => {
        try{
            setLoading(true);
            const result = await buyerDataProvider.getDefaultByuerData();
            if (result.data){
                values.name = result.data.name;
                values.surname = result.data.surname;
                values.companyName = result.data.companyName;
                values.nip = result.data.nip;
                values.streetName = result.data.streetName;
                values.houseNumber = result.data.houseNumber;
                values.apartmentNumber =  result.data.apartmentNumber;
                values.postcode = result.data.postcode;
                values.cityName = result.data.cityName;
            }
        }
        catch(error){
            if (axios.isAxiosError(error) &&
                error.response.status === 404)
                return;
            console.error(error);
        }
        finally{
            setLoading(false);
        }
    }

    useEffect(() => {
        fetchData();
    }, []);

    return (
        <>
            <Loading loading={loading} withBackdrop />
            <Box className={clsx([classes.formSection, className])}>
                <Box className={classes.formSectionHeader}>
                    {t('provideInvoiceData')}
                </Box>
                <Box
                    className={classes.buyerDataType}
                >
                    <CustomButton
                        type="button"
                        variant="outlined"
                        color="secondary"
                        startIcon={<PersonOutline />}
                        className={individualPersonButtonClasses}
                        onClick={() => switchBuyerDataType('IndividualPerson')}
                        fullWidth
                    >
                        {t('invoiceIndividual')}
                    </CustomButton>
                    <CustomButton
                        type="button"
                        variant="outlined"
                        color="secondary"
                        startIcon={<WorkOutline />}
                        className={companyButtonClasses}
                        onClick={() => switchBuyerDataType('Company')}
                        fullWidth
                    >
                        {t('invoiceCompany')}
                    </CustomButton>
                </Box>
                <Grid
                    container
                    spacing={1}
                    className={classes.buyerDataForm}
                >
                    {values.type === ('IndividualPerson' as BuyerDataType) && (
                        <>
                            <Grid item md={6} xs={12}>
                                <CustomTextField
                                    id="name"
                                    name="name"
                                    type="text"
                                    label={t('firstName') + individualPersonRequiredIndicator}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.name}
                                    helperText={touched.name && t(errors.name)}
                                    error={touched.name && !!errors.name}
                                    fullWidth
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CustomTextField
                                    id="surname"
                                    name="surname"
                                    type="text"
                                    label={t('lastName') + individualPersonRequiredIndicator}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.surname}
                                    helperText={
                                        touched.surname && t(errors.surname)
                                    }
                                    error={touched.surname && !!errors.surname}
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}
                    {values.type === ('Company' as BuyerDataType) && (
                        <>
                            <Grid item md={6} xs={12}>
                                <CustomTextField
                                    id="companyName"
                                    name="companyName"
                                    type="text"
                                    label={t('companyName') + companyRequiredIndicator}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.companyName}
                                    helperText={
                                        touched.companyName &&
                                        t(errors.companyName)
                                    }
                                    error={
                                        touched.companyName &&
                                        !!errors.companyName
                                    }
                                    fullWidth
                                />
                            </Grid>
                            <Grid item md={6} xs={12}>
                                <CustomTextField
                                    id="nip"
                                    name="nip"
                                    type="text"
                                    label={t('nip') + companyRequiredIndicator}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    value={values.nip}
                                    helperText={touched.nip && t(errors.nip)}
                                    error={touched.nip && !!errors.nip}
                                    fullWidth
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item md={6} xs={12}>
                        <CustomTextField
                            id="streetName"
                            name="streetName"
                            type="text"
                            label={t('street') + '*'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.streetName}
                            helperText={
                                touched.streetName && t(errors.streetName)
                            }
                            error={touched.streetName && !!errors.streetName}
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <CustomTextField
                            id="houseNumber"
                            name="houseNumber"
                            type="text"
                            label={t('number') + '*'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.houseNumber}
                            helperText={
                                touched.houseNumber && t(errors.houseNumber)
                            }
                            error={
                                touched.houseNumber && !!errors.houseNumber
                            }
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <CustomTextField
                            id="apartmentNumber"
                            name="apartmentNumber"
                            type="text"
                            label={t('apartment')}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.apartmentNumber}
                            helperText={
                                touched.apartmentNumber &&
                                t(errors.apartmentNumber)
                            }
                            error={
                                touched.apartmentNumber &&
                                !!errors.apartmentNumber
                            }
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={3} xs={12}>
                        <CustomTextField
                            id="postcode"
                            name="postcode"
                            type="text"
                            label={t('code') + '*'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.postcode}
                            helperText={
                                touched.postcode && t(errors.postcode)
                            }
                            error={touched.postcode && !!errors.postcode}
                            fullWidth
                        />
                    </Grid>
                    <Grid item md={9} xs={12}>
                        <CustomTextField
                            id="cityName"
                            name="cityName"
                            type="text"
                            label={t('city') + '*'}
                            onChange={handleChange}
                            onBlur={handleBlur}
                            value={values.cityName}
                            helperText={
                                touched.cityName && t(errors.cityName)
                            }
                            error={touched.cityName && !!errors.cityName}
                            fullWidth
                        />
                    </Grid>
                </Grid>
            </Box>
        </>
    );
};

type LabelProps = {
    htmlFor: string;
    children: ReactNode;
};

const Label = ({htmlFor, children}: LabelProps) => {
    const useStyles = makeStyles(() =>
        createStyles({
            labelContainer: {
                marginBottom: mobile.contentSmallerSpacing,
            },
        }),
    );

    const classes = useStyles();

    return (
        <Box className={classes.labelContainer}>
            <label htmlFor={htmlFor}>{children}</label>
        </Box>
    );
};

export default BuyerDataForm;
